import {Alert, Box, Button, Group, Loader, Paper, Stack, Text, Title} from '@mantine/core'
import {useForm} from '@mantine/form'
import {useEffect, useRef, useState} from 'react'

import {
    getActiveProjectBillingTerms,
    getProjectBillingTermsSummaryFields,
} from '../jobs/billing-terms-display'
import {useEditRights} from '../jobs/edit-rights-context'
import {StepBillingTerms} from '../jobs/step-billing-terms'
import {useTransportationRatePolicy} from '../settings/use-transportation-rate-policy'
import {type CreateProjectFormValues} from './create-project-form-values'
import {validateBillingTerms} from './create-project-validation'
import type {JobWithLineItems} from './project-billing-terms-utils'
import {
    buildProjectBillingTermsInput,
    canEditProjectBillingTerms,
    projectBillingTermsToFormValues,
    toWizardFormValues,
} from './project-billing-terms-utils'
import type {ProjectBillingTerms, ProjectJobSummary} from './schemas'
import {useCreateProjectBillingTerms, useProjectBillingTerms} from './use-projects'

type ProjectBillingTermsSectionProps = {
    projectId: string
    jobs: ProjectJobSummary[]
}

function BillingTermsField({label, value}: {label: string; value: string}) {
    return (
        <Stack gap={0} style={{flexShrink: 0}}>
            <Text size="xs" fw={700} c="dimmed" tt="uppercase" style={{letterSpacing: '0.04em'}}>
                {label}
            </Text>
            <Text size="sm" fw={600}>
                {value}
            </Text>
        </Stack>
    )
}

type ProjectBillingTermsSummaryProps = {
    isLoading: boolean
    isError: boolean
    activeTerms: ProjectBillingTerms | null
}

export function ProjectBillingTermsSummary({
    isLoading,
    isError,
    activeTerms,
}: ProjectBillingTermsSummaryProps) {
    if (isLoading) {
        return <Loader size="sm" role="status" aria-label="Loading billing terms" />
    }

    if (isError) {
        return (
            <Text size="sm" c="red">
                Could not load billing terms.
            </Text>
        )
    }

    if (activeTerms == null) {
        return (
            <Text size="sm" c="dimmed">
                No billing terms have been set for this order.
            </Text>
        )
    }

    return (
        <>
            <Box
                style={{
                    width: 1,
                    alignSelf: 'stretch',
                    background: 'var(--mantine-color-gray-3)',
                    flexShrink: 0,
                }}
            />
            <Group gap="lg" wrap="wrap" style={{flex: 1, minWidth: 0}}>
                {getProjectBillingTermsSummaryFields(activeTerms).map((field) => (
                    <BillingTermsField key={field.label} label={field.label} value={field.value} />
                ))}
            </Group>
        </>
    )
}

export function ProjectBillingTermsSection({projectId, jobs}: ProjectBillingTermsSectionProps) {
    const {canEdit} = useEditRights()
    const editable = canEditProjectBillingTerms(jobs as JobWithLineItems[])
    const hasActiveJobs = jobs.some((job) => job.status === 'active')
    const {data: billingTerms = [], isLoading, isError} = useProjectBillingTerms(projectId)
    const activeTerms = getActiveProjectBillingTerms(billingTerms)
    const createBillingTerms = useCreateProjectBillingTerms(projectId)
    const [isEditing, setIsEditing] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const {data: policy} = useTransportationRatePolicy()
    const activeMinimumRef = useRef<number | null>(null)
    activeMinimumRef.current = policy?.minimumHourlyRate ?? null

    const form = useForm<CreateProjectFormValues>({
        initialValues: toWizardFormValues(projectBillingTermsToFormValues(activeTerms)),
        validate: (values) => validateBillingTerms(values, activeMinimumRef.current),
    })

    useEffect(() => {
        if (!isEditing) {
            form.setValues(toWizardFormValues(projectBillingTermsToFormValues(activeTerms)))
        }
    }, [activeTerms?.id, isEditing])

    function startEditing() {
        form.setValues(toWizardFormValues(projectBillingTermsToFormValues(activeTerms)))
        setErrorMessage(null)
        setIsEditing(true)
    }

    function handleCancel() {
        form.setValues(toWizardFormValues(projectBillingTermsToFormValues(activeTerms)))
        setErrorMessage(null)
        setIsEditing(false)
    }

    async function handleSave() {
        const validation = form.validate()
        if (validation.hasErrors) {
            return
        }

        setErrorMessage(null)
        try {
            await createBillingTerms.mutateAsync(
                buildProjectBillingTermsInput(
                    {
                        billingMethod: form.values.billingMethod,
                        billingTerms: form.values.billingTerms,
                    },
                    activeTerms,
                ),
            )
            setIsEditing(false)
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to save billing terms')
        }
    }

    return (
        <Paper withBorder p="md" radius="md">
            <Stack gap="md">
                {isEditing ? (
                    <>
                        <Group justify="space-between" align="center">
                            <Title order={4}>Billing terms</Title>
                            <Group gap="sm">
                                <Button
                                    variant="default"
                                    size="compact-sm"
                                    onClick={handleCancel}
                                    disabled={createBillingTerms.isPending}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    size="compact-sm"
                                    onClick={() => void handleSave()}
                                    loading={createBillingTerms.isPending}
                                >
                                    Save
                                </Button>
                            </Group>
                        </Group>

                        {hasActiveJobs ? (
                            <Alert color="yellow" variant="light">
                                <Text size="sm">
                                    This order has active jobs. Changing billing terms may affect
                                    calculations for work already in progress. No existing billable
                                    line items will be modified.
                                </Text>
                            </Alert>
                        ) : null}

                        {errorMessage ? (
                            <Text size="sm" c="red">
                                {errorMessage}
                            </Text>
                        ) : null}

                        <StepBillingTerms form={form} showManualCharges={false} />
                    </>
                ) : (
                    <>
                        <Group gap="md" wrap="nowrap" align="center">
                            <Title order={4} style={{flexShrink: 0}}>
                                Billing terms
                            </Title>

                            <ProjectBillingTermsSummary
                                isLoading={isLoading}
                                isError={isError}
                                activeTerms={activeTerms}
                            />

                            {editable && canEdit ? (
                                <Button
                                    variant="subtle"
                                    size="compact-sm"
                                    onClick={startEditing}
                                    style={{flexShrink: 0}}
                                >
                                    Edit
                                </Button>
                            ) : null}
                        </Group>

                        {!editable ? (
                            <Text size="sm" c="dimmed">
                                Billing terms can only be edited before any line items have been
                                invoiced.
                            </Text>
                        ) : null}
                    </>
                )}
            </Stack>
        </Paper>
    )
}
